import * as React from "react";
import { Lock, LockKeyholeOpen } from "lucide-react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import type { ShareAccess } from "@/components/share/types";

interface Props {
  access: ShareAccess;
  isReadOnly: boolean;
  onAccessChange: (access: ShareAccess) => Promise<void>;
}

interface FormInputs {
  access: ShareAccess;
}

export const ShareGeneralAccess: React.FC<Props> = ({ access, isReadOnly, onAccessChange }) => {
  const methods = useForm<FormInputs>({
    defaultValues: {
      access,
    },
  });

  const newAccess = useWatch({
    name: "access",
    control: methods.control,
  });

  const updatePermission = React.useCallback(
    async (nextAccess: ShareAccess) => {
      await onAccessChange(nextAccess);
      toast.success("General access change!");
    },
    [onAccessChange],
  );

  React.useEffect(() => {
    if (access !== newAccess) {
      void updatePermission(newAccess);
    }
  }, [newAccess, access, updatePermission]);

  return (
    <div className="mt-3">
      <FormProvider {...methods}>
        <h4 className="text-sm font-semibold">General access</h4>
        <div className="flex">
          <div className="mr-2 mt-3 text-gray-500">{access === "restricted" ? <Lock /> : <LockKeyholeOpen />}</div>
          <div>
            <select
              disabled={isReadOnly}
              {...methods.register("access")}
              className="mt-2 block rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm/6"
            >
              <option value="restricted">Restricted</option>
              <option value="public">Visible for everone</option>
            </select>
            <p className="mt-1 text-sm text-gray-500">
              {access === "restricted"
                ? "Only people assigned can access this item"
                : "Everyone in your organization can access this item"}
            </p>
          </div>
        </div>
      </FormProvider>
    </div>
  );
};
